/**
 * EMZI NEXUS V6 — Phase B4: Shared Compliance Engine
 * Unified compliance checking and scoring logic across all studios
 */

import axios from 'axios';
import { txt } from './bilingualText';
import { BACKEND_URL } from './backendUrl';

// Cache for active packs (2-5 min TTL)
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes
let packsCache = {
  data: null,
  timestamp: null,
  key: null
};

/**
 * Get cached packs or fetch fresh data
 * @param {string} studioType - Studio type (content/copy/live)
 * @param {Array} productIds - Product IDs
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Array>} Active packs
 */
export const getActivePacks = async (studioType, productIds = [], campaignId = null) => {
  const cacheKey = `${studioType}-${JSON.stringify(productIds)}-${campaignId}`;
  const now = Date.now();

  // Check cache validity
  if (packsCache.data && packsCache.key === cacheKey && 
      (now - packsCache.timestamp) < CACHE_TTL) {
    console.log('📦 Using cached packs');
    return packsCache.data;
  }

  // Fetch fresh data
  try {
    const response = await axios.post(`${BACKEND_URL}/api/v3/studios/active-packs`, {
      studio_type: studioType,
      product_ids: productIds,
      campaign_id: campaignId
    });

    if (response.data.status === 'success') {
      const packs = response.data.active_packs || [];
      
      // Update cache
      packsCache = {
        data: packs,
        timestamp: now,
        key: cacheKey
      };

      console.log(`✅ Loaded ${packs.length} active packs`);
      return packs;
    }
  } catch (err) {
    console.error('❌ Error fetching active packs:', err);
    return [];
  }

  return [];
};

/**
 * Check text compliance against active packs
 * @param {string} text - Text to check
 * @param {string} studioType - Studio type
 * @param {Array} productIds - Product IDs
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>} Compliance result
 */
export const checkCompliance = async (text, studioType, productIds = [], campaignId = null) => {
  if (!text || text.trim().length === 0) {
    return {
      status: 'safe',
      flagged_phrases: [],
      compliance_score: 100,
      color: '🟢'
    };
  }

  try {
    const response = await axios.post(`${BACKEND_URL}/api/v3/studios/compliance-check`, {
      text,
      studio_type: studioType,
      product_ids: productIds,
      campaign_id: campaignId
    });

    const result = response.data;
    
    // Add color code based on status
    result.color = getStatusColor(result.status);
    
    return result;
  } catch (err) {
    console.error('❌ Error checking compliance:', err);
    return {
      status: 'error',
      flagged_phrases: [],
      compliance_score: 0,
      color: '⚪',
      error: 'Failed to check compliance'
    };
  }
};

/**
 * Get color code for compliance status
 * @param {string} status - Compliance status (safe/warning/blocked)
 * @returns {string} Color emoji
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'safe':
      return '🟢';
    case 'warning':
      return '🟡';
    case 'blocked':
      return '🔴';
    default:
      return '⚪';
  }
};

/**
 * Get compliance score color (for visual indicators)
 * @param {number} score - Compliance score (0-100)
 * @returns {string} Hex color code
 */
export const getScoreColor = (score) => {
  if (score >= 80) return '#10B981'; // Green
  if (score >= 60) return '#F59E0B'; // Yellow
  return '#EF4444'; // Red
};

/**
 * Extract tone guidelines from active packs
 * @param {Array} packs - Active packs
 * @returns {Array} Tone guidelines
 */
export const extractToneGuidelines = (packs) => {
  const guidelines = [];

  packs.forEach(pack => {
    const content = pack.content || {};
    const aiContext = pack.ai_context || {};

    // From Values packs
    if (pack.pack_type === 'values' && content.core_values) {
      guidelines.push({
        source: txt(pack.name, 'en', 'Values Pack'),
        guideline: 'Align with core values',
        influence: aiContext.tone_influence || 0.5,
        pack_id: pack.pack_id
      });
    }

    // From Direction packs
    if (pack.pack_type === 'direction' && content.strategic_direction) {
      guidelines.push({
        source: txt(pack.name, 'en', 'Direction Pack'),
        guideline: 'Follow strategic direction',
        influence: aiContext.tone_influence || 0.5,
        pack_id: pack.pack_id
      });
    }
  });

  return guidelines;
};

/**
 * Extract CTA examples from active packs
 * @param {Array} packs - Active packs
 * @returns {Array} CTA examples
 */
export const extractCTAExamples = (packs) => {
  const ctas = [];

  packs.forEach(pack => {
    const content = pack.content || {};

    // From Promotion packs
    if (pack.pack_type === 'promotion' && content.call_to_action) {
      ctas.push({
        text: content.call_to_action,
        source: txt(pack.name, 'en', 'Promotion Pack'),
        pack_id: pack.pack_id
      });
    }
  });

  return ctas;
};

/**
 * Log compliance event for analytics
 * @param {string} eventType - Event type (check/flag/resolve)
 * @param {Object} data - Event data
 */
export const logComplianceEvent = (eventType, data) => {
  // Log to console for now, can be extended to backend analytics
  console.log(`📊 Compliance Event [${eventType}]:`, {
    timestamp: new Date().toISOString(),
    ...data
  });
  
  // TODO: Send to backend analytics endpoint when ready
  // axios.post(`${BACKEND_URL}/api/v3/analytics/compliance-events`, {
  //   event_type: eventType,
  //   ...data
  // });
};

/**
 * Clear packs cache (useful for manual refresh)
 */
export const clearPacksCache = () => {
  packsCache = {
    data: null,
    timestamp: null,
    key: null
  };
  console.log('🗑️ Packs cache cleared');
};

export default {
  getActivePacks,
  checkCompliance,
  getStatusColor,
  getScoreColor,
  extractToneGuidelines,
  extractCTAExamples,
  logComplianceEvent,
  clearPacksCache
};
